import {
  findMappingTargetBySemanticField,
  findSemanticFieldDefinition,
  findSemanticField,
  semanticFieldDefinitions,
} from '@/modules/file-reader/mappingTargets'
import type {
  DeclarativeFileLayout,
  DeclarativeLayoutField,
  FileLayoutDataType,
  FileLayoutDirection,
  FileLayoutFormat,
  FileLayoutValidationResult,
  FileLayoutWithVersion,
  FiscalDocumentType,
  SaveFileLayoutInput,
} from '@/modules/file-reader'

const DIRECTIONS: FileLayoutDirection[] = ['import', 'export', 'bidirectional']
const FORMATS: FileLayoutFormat[] = ['xml', 'delimited_text', 'fixed_width_text']
const DATA_TYPES: FileLayoutDataType[] = ['string', 'date', 'number', 'integer', 'boolean']
const MAX_GROUPS = 100
const MAX_FIELDS = 5000

const BUILT_IN_NAMES: Partial<Record<FiscalDocumentType, string>> = {
  nfe: 'NF-e padrão nacional',
  cte: 'CT-e padrão nacional',
  nfse_nacional: 'NFS-e padrão nacional',
  sped_fiscal: 'SPED Fiscal — importar histórico',
  sped_contributions: 'SPED Contribuições — importar histórico',
}

const BUILT_IN_FORMATS: Partial<Record<FiscalDocumentType, FileLayoutFormat>> = {
  nfe: 'xml',
  cte: 'xml',
  nfse_nacional: 'xml',
  sped_fiscal: 'delimited_text',
  sped_contributions: 'delimited_text',
}

const BUILT_IN_DESCRIPTIONS: Partial<Record<FiscalDocumentType, string>> = {
  sped_fiscal: 'Importação histórica básica de participantes, produtos, NF-e e CT-e de entrada e saída da EFD ICMS/IPI.',
  sped_contributions: 'Importação histórica de participantes, produtos, NF-e individualizadas ou consolidadas de entrada e saída e aquisições por CT-e da EFD Contribuições.',
}

const BUILT_IN_DETECTION: Partial<Record<FiscalDocumentType, DeclarativeFileLayout['detection']>> = {
  nfe: {
    rootName: 'nfeProc',
    namespaceUri: 'http://www.portalfiscal.inf.br/nfe',
    requiredPath: '/nfeProc/NFe/infNFe',
  },
  cte: {
    rootName: 'cteProc',
    namespaceUri: 'http://www.portalfiscal.inf.br/cte',
    requiredPath: '/cteProc/CTe/infCte',
  },
  nfse_nacional: {
    rootName: 'NFSe',
    namespaceUri: 'http://www.sped.fazenda.gov.br/nfse',
    requiredPath: '/NFSe/infNFSe',
  },
  sped_fiscal: { fileNamePattern: '*.txt', recordType: 'SPED_EFD_ICMS_IPI' },
  sped_contributions: { fileNamePattern: '*.txt', recordType: 'SPED_EFD_CONTRIBUICOES' },
}

function layoutField(
  key: string,
  label: string,
  externalPath: string,
  internalField: string,
  dataType: FileLayoutDataType = 'string',
  required = false,
): DeclarativeLayoutField {
  return { key, label, externalPath, internalField, dataType, required }
}

function digitsLayoutField(
  key: string,
  label: string,
  externalPath: string,
  internalField: string,
): DeclarativeLayoutField {
  return { ...layoutField(key, label, externalPath, internalField), transform: { operation: 'digits' } }
}

function spedField(
  key: string,
  label: string,
  position: number,
  internalField: string,
  dataType: FileLayoutDataType = 'string',
  required = false,
) {
  return layoutField(key, label, `field:${position}`, internalField, dataType, required)
}

function spedFiscalHeaderGroup(): DeclarativeFileLayout['groups'] {
  return [{
    key: 'file', label: 'Escrituração e estabelecimento (0000)', externalPath: '0000', repeat: false, required: true, fields: [
      spedField('layoutVersion', 'Versão do leiaute', 2, 'fiscal.file.layoutVersion'),
      spedField('purpose', 'Finalidade do arquivo', 3, 'fiscal.file.purpose'),
      spedField('periodStart', 'Início do período', 4, 'fiscal.file.period.start', 'date', true),
      spedField('periodEnd', 'Fim do período', 5, 'fiscal.file.period.end', 'date', true),
      spedField('establishmentName', 'Nome empresarial', 6, 'fiscal.establishment.name', 'string', true),
      spedField('establishmentTaxId', 'CPF/CNPJ do estabelecimento', 7, 'fiscal.establishment.taxId', 'string', true),
      spedField('establishmentState', 'UF', 9, 'fiscal.establishment.state'),
      spedField('stateRegistration', 'Inscrição estadual', 10, 'fiscal.establishment.stateRegistration'),
      spedField('municipalityCode', 'Código do município', 11, 'fiscal.establishment.municipalityCode'),
      spedField('municipalRegistration', 'Inscrição municipal', 12, 'fiscal.establishment.municipalRegistration'),
      spedField('suframa', 'Inscrição SUFRAMA', 13, 'fiscal.establishment.suframa'),
      spedField('fiscalProfile', 'Perfil de apresentação fiscal', 14, 'fiscal.establishment.fiscalProfile'),
      spedField('activityIndicator', 'Indicador de atividade', 15, 'fiscal.establishment.activityIndicator'),
    ],
  }]
}

function spedContributionsHeaderGroups(): DeclarativeFileLayout['groups'] {
  return [
    {
      key: 'file', label: 'Escrituração (0000)', externalPath: '0000', repeat: false, required: true, fields: [
        spedField('layoutVersion', 'Versão do leiaute', 2, 'fiscal.file.layoutVersion'),
        spedField('specialSituation', 'Situação especial', 4, 'fiscal.establishment.specialSituation'),
        spedField('periodStart', 'Início do período', 6, 'fiscal.file.period.start', 'date', true),
        spedField('periodEnd', 'Fim do período', 7, 'fiscal.file.period.end', 'date', true),
        spedField('establishmentName', 'Nome empresarial', 8, 'fiscal.establishment.name', 'string', true),
        spedField('establishmentTaxId', 'CNPJ da matriz', 9, 'fiscal.establishment.taxId', 'string', true),
        spedField('establishmentState', 'UF', 10, 'fiscal.establishment.state'),
        spedField('municipalityCode', 'Código do município', 11, 'fiscal.establishment.municipalityCode'),
        spedField('suframa', 'Inscrição SUFRAMA', 12, 'fiscal.establishment.suframa'),
        spedField('legalNatureIndicator', 'Natureza da pessoa jurídica', 13, 'fiscal.establishment.legalNatureIndicator'),
        spedField('activityIndicator', 'Atividade preponderante', 14, 'fiscal.establishment.activityIndicator'),
      ],
    },
    {
      key: 'taxProfile', label: 'Regime de PIS e COFINS (0110)', externalPath: '0110', repeat: false, fields: [
        spedField('pisCofinsIncidence', 'Incidência tributária', 2, 'fiscal.establishment.taxProfile.pisCofinsIncidence'),
        spedField('creditMethod', 'Método de apropriação de créditos', 3, 'fiscal.establishment.taxProfile.creditMethod'),
        spedField('accountingMethod', 'Método contábil', 4, 'fiscal.establishment.taxProfile.accountingMethod'),
        spedField('cumulativeRegimeIndicator', 'Regime cumulativo', 5, 'fiscal.establishment.taxProfile.cumulativeRegimeIndicator'),
      ],
    },
    {
      key: 'establishments', label: 'Estabelecimentos (0140)', externalPath: '0140', repeat: true, fields: [
        spedField('externalCode', 'Código do estabelecimento', 2, 'fiscal.establishment.externalCode'),
        spedField('name', 'Nome empresarial', 3, 'fiscal.establishment.name', 'string', true),
        spedField('taxId', 'CNPJ', 4, 'fiscal.establishment.taxId', 'string', true),
        spedField('state', 'UF', 5, 'fiscal.establishment.state'),
        spedField('stateRegistration', 'Inscrição estadual', 6, 'fiscal.establishment.stateRegistration'),
        spedField('municipalityCode', 'Código do município', 7, 'fiscal.establishment.municipalityCode'),
      ],
    },
  ]
}

function spedReferenceGroups(includeCest: boolean): DeclarativeFileLayout['groups'] {
  const productFields = [
    spedField('externalCode', 'Código do item', 2, 'fiscal.product.externalCode', 'string', true),
    spedField('description', 'Descrição do item', 3, 'fiscal.product.description', 'string', true),
    spedField('barcode', 'Código de barras', 4, 'fiscal.product.barcode'),
    spedField('previousCode', 'Código anterior', 5, 'fiscal.product.previousCode'),
    {
      ...spedField('inventoryUom', 'Unidade de inventário', 6, 'fiscal.product.inventoryUom'),
      reference: { group: 'uoms', field: 'externalCode' },
    },
    spedField('sourceItemType', 'Tipo do item informado no SPED', 7, 'fiscal.product.sourceItemType'),
    spedField('ncm', 'NCM', 8, 'fiscal.product.ncm'),
    spedField('ipiException', 'Exceção do IPI', 9, 'fiscal.product.ipiException'),
    spedField('genericCode', 'Código do gênero', 10, 'fiscal.product.genericCode'),
    spedField('serviceCode', 'Código do serviço', 11, 'fiscal.product.serviceCode'),
    spedField('icmsRate', 'Alíquota interna do ICMS', 12, 'fiscal.product.icmsRate', 'number'),
  ]
  if (includeCest) productFields.push(spedField('cest', 'CEST', 13, 'fiscal.product.cest'))
  return [
    {
      key: 'participants', label: 'Participantes (0150)', externalPath: '0150', repeat: true, fields: [
        spedField('externalCode', 'Código do participante', 2, 'fiscal.participant.externalCode', 'string', true),
        spedField('name', 'Nome', 3, 'fiscal.participant.name', 'string', true),
        spedField('countryCode', 'Código do país', 4, 'fiscal.participant.countryCode'),
        spedField('taxId', 'CPF/CNPJ', 5, 'fiscal.participant.taxId'),
        spedField('stateRegistration', 'Inscrição estadual', 7, 'fiscal.participant.stateRegistration'),
        spedField('municipalityCode', 'Código do município', 8, 'fiscal.participant.municipalityCode'),
        spedField('street', 'Logradouro', 10, 'fiscal.participant.address.street'),
        spedField('number', 'Número', 11, 'fiscal.participant.address.number'),
        spedField('complement', 'Complemento', 12, 'fiscal.participant.address.complement'),
        spedField('district', 'Bairro', 13, 'fiscal.participant.address.district'),
      ],
    },
    {
      key: 'uoms', label: 'Unidades de medida (0190)', externalPath: '0190', repeat: true, fields: [
        spedField('externalCode', 'Código da unidade', 2, 'fiscal.uom.externalCode', 'string', true),
        spedField('description', 'Descrição da unidade', 3, 'fiscal.uom.description', 'string', true),
      ],
    },
    {
      key: 'products', label: 'Produtos e serviços (0200)', externalPath: '0200', repeat: true, fields: productFields,
    },
  ]
}

function spedNfeGroups(includeIcmsSummary: boolean, parent: string | null = null): DeclarativeFileLayout['groups'] {
  const groups: DeclarativeFileLayout['groups'] = [
    {
      key: 'nfeDocuments', label: 'NF-e de entrada e saída (C100)', externalPath: 'C100', repeat: true, parent, fields: [
        spedField('operationIndicator', 'Indicador da operação', 2, 'fiscal.document.operationIndicator', 'string', true),
        spedField('issueIndicator', 'Indicador do emitente', 3, 'fiscal.document.issueIndicator'),
        {
          ...spedField('partyCode', 'Código do participante', 4, 'fiscal.document.partyCode'),
          reference: { group: 'participants', field: 'externalCode' },
        },
        spedField('model', 'Modelo do documento', 5, 'fiscal.document.model'),
        spedField('statusCode', 'Situação do documento', 6, 'fiscal.document.statusCode'),
        spedField('series', 'Série', 7, 'fiscal.document.series'),
        spedField('number', 'Número', 8, 'fiscal.document.number', 'string', true),
        spedField('accessKey', 'Chave da NF-e', 9, 'fiscal.document.accessKey'),
        spedField('issuedAt', 'Data de emissão', 10, 'fiscal.document.issuedAt', 'date', true),
        spedField('entryExitAt', 'Data de entrada ou saída', 11, 'fiscal.document.entryExitAt', 'date'),
        spedField('total', 'Valor do documento', 12, 'fiscal.document.total', 'number'),
        spedField('discount', 'Valor do desconto', 14, 'fiscal.document.discount', 'number'),
        spedField('icmsBase', 'Base total do ICMS', 21, 'fiscal.document.taxes.icms.base', 'number'),
        spedField('icmsAmount', 'Valor total do ICMS', 22, 'fiscal.document.taxes.icms.amount', 'number'),
        spedField('ipiAmount', 'Valor total do IPI', 25, 'fiscal.document.taxes.ipi.amount', 'number'),
        spedField('pisAmount', 'Valor total do PIS', 26, 'fiscal.document.taxes.pis.amount', 'number'),
        spedField('cofinsAmount', 'Valor total da COFINS', 27, 'fiscal.document.taxes.cofins.amount', 'number'),
      ],
    },
    {
      key: 'nfeItems', label: 'Itens das NF-e (C170)', externalPath: 'C170', repeat: true, parent: 'nfeDocuments', fields: [
        spedField('lineNumber', 'Número do item', 2, 'fiscal.document.items.lineNumber', 'integer', true),
        {
          ...spedField('productCode', 'Código do produto', 3, 'fiscal.document.items.productCode', 'string', true),
          reference: { group: 'products', field: 'externalCode' },
        },
        spedField('complementaryDescription', 'Descrição complementar', 4, 'fiscal.document.items.complementaryDescription'),
        spedField('quantity', 'Quantidade', 5, 'fiscal.document.items.quantity', 'number'),
        spedField('uom', 'Unidade', 6, 'fiscal.document.items.uom'),
        spedField('total', 'Valor do item', 7, 'fiscal.document.items.total', 'number'),
        spedField('discount', 'Desconto do item', 8, 'fiscal.document.items.discount', 'number'),
        spedField('movementIndicator', 'Movimentação física', 9, 'fiscal.document.items.movementIndicator'),
        spedField('icmsSituation', 'CST do ICMS', 10, 'fiscal.document.items.taxes.icms.situation'),
        spedField('cfop', 'CFOP', 11, 'fiscal.document.items.cfop'),
        spedField('icmsBase', 'Base do ICMS', 13, 'fiscal.document.items.taxes.icms.base', 'number'),
        spedField('icmsRate', 'Alíquota do ICMS', 14, 'fiscal.document.items.taxes.icms.rate', 'number'),
        spedField('icmsAmount', 'Valor do ICMS', 15, 'fiscal.document.items.taxes.icms.amount', 'number'),
        spedField('ipiSituation', 'CST do IPI', 20, 'fiscal.document.items.taxes.ipi.situation'),
        spedField('ipiBase', 'Base do IPI', 22, 'fiscal.document.items.taxes.ipi.base', 'number'),
        spedField('ipiRate', 'Alíquota do IPI', 23, 'fiscal.document.items.taxes.ipi.rate', 'number'),
        spedField('ipiAmount', 'Valor do IPI', 24, 'fiscal.document.items.taxes.ipi.amount', 'number'),
        spedField('pisSituation', 'CST do PIS', 25, 'fiscal.document.items.taxes.pis.situation'),
        spedField('pisBase', 'Base do PIS', 26, 'fiscal.document.items.taxes.pis.base', 'number'),
        spedField('pisRate', 'Alíquota do PIS', 27, 'fiscal.document.items.taxes.pis.rate', 'number'),
        spedField('pisAmount', 'Valor do PIS', 30, 'fiscal.document.items.taxes.pis.amount', 'number'),
        spedField('cofinsSituation', 'CST da COFINS', 31, 'fiscal.document.items.taxes.cofins.situation'),
        spedField('cofinsBase', 'Base da COFINS', 32, 'fiscal.document.items.taxes.cofins.base', 'number'),
        spedField('cofinsRate', 'Alíquota da COFINS', 33, 'fiscal.document.items.taxes.cofins.rate', 'number'),
        spedField('cofinsAmount', 'Valor da COFINS', 36, 'fiscal.document.items.taxes.cofins.amount', 'number'),
      ],
    },
  ]
  if (includeIcmsSummary) groups.push({
    key: 'nfeTaxSummaries', label: 'Resumos fiscais das NF-e (C190)', externalPath: 'C190', repeat: true, parent: 'nfeDocuments', fields: [
      spedField('icmsSituation', 'CST do ICMS', 2, 'fiscal.document.taxSummaries.taxes.icms.situation', 'string', true),
      spedField('cfop', 'CFOP', 3, 'fiscal.document.taxSummaries.cfop', 'string', true),
      spedField('icmsRate', 'Alíquota do ICMS', 4, 'fiscal.document.taxSummaries.taxes.icms.rate', 'number'),
      spedField('operationTotal', 'Valor da operação', 5, 'fiscal.document.taxSummaries.operationTotal', 'number'),
      spedField('icmsBase', 'Base do ICMS', 6, 'fiscal.document.taxSummaries.taxes.icms.base', 'number'),
      spedField('icmsAmount', 'Valor do ICMS', 7, 'fiscal.document.taxSummaries.taxes.icms.amount', 'number'),
      spedField('icmsStBase', 'Base do ICMS ST', 8, 'fiscal.document.taxSummaries.taxes.icms.stBase', 'number'),
      spedField('icmsStAmount', 'Valor do ICMS ST', 9, 'fiscal.document.taxSummaries.taxes.icms.stAmount', 'number'),
      spedField('reducedBase', 'Redução da base do ICMS', 10, 'fiscal.document.taxSummaries.taxes.icms.reducedBase', 'number'),
      spedField('ipiAmount', 'Valor do IPI', 11, 'fiscal.document.taxSummaries.taxes.ipi.amount', 'number'),
    ],
  })
  return groups
}

function spedFiscalCteGroups(): DeclarativeFileLayout['groups'] {
  return [
    {
      key: 'cteDocuments', label: 'CT-e de entrada e saída (D100)', externalPath: 'D100', repeat: true, fields: [
        spedField('operationIndicator', 'Indicador da operação', 2, 'fiscal.document.operationIndicator', 'string', true),
        spedField('issueIndicator', 'Indicador do emitente', 3, 'fiscal.document.issueIndicator'),
        {
          ...spedField('partyCode', 'Código do participante', 4, 'fiscal.document.partyCode'),
          reference: { group: 'participants', field: 'externalCode' },
        },
        spedField('model', 'Modelo do documento', 5, 'fiscal.document.model'),
        spedField('statusCode', 'Situação do documento', 6, 'fiscal.document.statusCode'),
        spedField('series', 'Série', 7, 'fiscal.document.series'),
        spedField('number', 'Número', 9, 'fiscal.document.number', 'string', true),
        spedField('accessKey', 'Chave do CT-e', 10, 'fiscal.document.accessKey'),
        spedField('issuedAt', 'Data de emissão', 11, 'fiscal.document.issuedAt', 'date', true),
        spedField('entryExitAt', 'Data de aquisição ou prestação', 12, 'fiscal.document.entryExitAt', 'date'),
        spedField('total', 'Valor do documento', 15, 'fiscal.document.total', 'number'),
        spedField('discount', 'Valor do desconto', 16, 'fiscal.document.discount', 'number'),
        spedField('icmsBase', 'Base do ICMS', 19, 'fiscal.document.taxes.icms.base', 'number'),
        spedField('icmsAmount', 'Valor do ICMS', 20, 'fiscal.document.taxes.icms.amount', 'number'),
      ],
    },
    {
      key: 'cteTaxSummaries', label: 'Resumos fiscais dos CT-e (D190)', externalPath: 'D190', repeat: true, parent: 'cteDocuments', fields: [
        spedField('icmsSituation', 'CST do ICMS', 2, 'fiscal.document.taxSummaries.taxes.icms.situation', 'string', true),
        spedField('cfop', 'CFOP', 3, 'fiscal.document.taxSummaries.cfop', 'string', true),
        spedField('icmsRate', 'Alíquota do ICMS', 4, 'fiscal.document.taxSummaries.taxes.icms.rate', 'number'),
        spedField('operationTotal', 'Valor da operação', 5, 'fiscal.document.taxSummaries.operationTotal', 'number'),
        spedField('icmsBase', 'Base do ICMS', 6, 'fiscal.document.taxSummaries.taxes.icms.base', 'number'),
        spedField('icmsAmount', 'Valor do ICMS', 7, 'fiscal.document.taxSummaries.taxes.icms.amount', 'number'),
        spedField('reducedBase', 'Redução da base do ICMS', 8, 'fiscal.document.taxSummaries.taxes.icms.reducedBase', 'number'),
      ],
    },
  ]
}

function spedContributionsConsolidatedNfeGroups(): DeclarativeFileLayout['groups'] {
  const consolidation = (
    key: string,
    label: string,
    record: 'C180' | 'C190',
  ): DeclarativeFileLayout['groups'][number] => ({
    key,
    label,
    externalPath: record,
    repeat: true,
    parent: 'nfeEstablishments',
    fields: [
      spedField('model', 'Modelo do documento', 2, 'fiscal.document.consolidations.model', 'string', true),
      spedField('periodStart', 'Início da consolidação', 3, 'fiscal.document.consolidations.period.start', 'date', true),
      spedField('periodEnd', 'Fim da consolidação', 4, 'fiscal.document.consolidations.period.end', 'date', true),
      {
        ...spedField('productCode', 'Código do produto', 5, 'fiscal.document.consolidations.productCode', 'string', true),
        reference: { group: 'products', field: 'externalCode' },
      },
      spedField('ncm', 'NCM', 6, 'fiscal.document.consolidations.ncm'),
      spedField('ipiException', 'Exceção do IPI', 7, 'fiscal.document.consolidations.ipiException'),
      spedField('total', 'Valor consolidado do item', 8, 'fiscal.document.consolidations.total', 'number', true),
    ],
  })
  const taxDetail = (
    key: string,
    label: string,
    record: 'C181' | 'C185' | 'C191' | 'C195',
    parent: 'consolidatedOutgoingNfe' | 'consolidatedIncomingNfe',
    tax: 'pis' | 'cofins',
    includesParty: boolean,
  ): DeclarativeFileLayout['groups'][number] => {
    const offset = includesParty ? 1 : 0
    return {
      key,
      label,
      externalPath: record,
      repeat: true,
      parent,
      fields: [
        ...(includesParty
          ? [spedField('partyTaxId', 'CPF/CNPJ do participante', 2, 'fiscal.document.taxDetails.partyTaxId')]
          : []),
        spedField('situation', `CST do ${tax === 'pis' ? 'PIS' : 'COFINS'}`, 2 + offset, `fiscal.document.taxDetails.taxes.${tax}.situation`, 'string', true),
        spedField('cfop', 'CFOP', 3 + offset, 'fiscal.document.taxDetails.cfop', 'string', true),
        spedField('itemAmount', 'Valor do item', 4 + offset, 'fiscal.document.taxDetails.itemAmount', 'number'),
        spedField('discount', 'Desconto ou exclusão', 5 + offset, 'fiscal.document.taxDetails.discount', 'number'),
        spedField('base', `Base do ${tax === 'pis' ? 'PIS' : 'COFINS'}`, 6 + offset, `fiscal.document.taxDetails.taxes.${tax}.base`, 'number'),
        spedField('rate', `Alíquota do ${tax === 'pis' ? 'PIS' : 'COFINS'}`, 7 + offset, `fiscal.document.taxDetails.taxes.${tax}.rate`, 'number'),
        spedField('amount', `Valor do ${tax === 'pis' ? 'PIS' : 'COFINS'}`, 10 + offset, `fiscal.document.taxDetails.taxes.${tax}.amount`, 'number'),
        spedField('accountCode', 'Código da conta', 11 + offset, 'fiscal.document.taxDetails.accountCode'),
      ],
    }
  }
  return [
    consolidation('consolidatedOutgoingNfe', 'Saídas consolidadas por produto (C180)', 'C180'),
    taxDetail('consolidatedOutgoingPis', 'PIS das saídas consolidadas (C181)', 'C181', 'consolidatedOutgoingNfe', 'pis', false),
    taxDetail('consolidatedOutgoingCofins', 'COFINS das saídas consolidadas (C185)', 'C185', 'consolidatedOutgoingNfe', 'cofins', false),
    consolidation('consolidatedIncomingNfe', 'Entradas consolidadas por produto (C190)', 'C190'),
    taxDetail('consolidatedIncomingPis', 'PIS das entradas consolidadas (C191)', 'C191', 'consolidatedIncomingNfe', 'pis', true),
    taxDetail('consolidatedIncomingCofins', 'COFINS das entradas consolidadas (C195)', 'C195', 'consolidatedIncomingNfe', 'cofins', true),
  ]
}

function spedContributionsBlockEstablishment(
  key: string,
  record: 'C010' | 'D010',
  label: string,
): DeclarativeFileLayout['groups'][number] {
  return {
    key,
    label,
    externalPath: record,
    repeat: true,
    fields: [
      {
        ...spedField('taxId', 'CNPJ do estabelecimento', 2, 'fiscal.establishment.taxId', 'string', true),
        reference: { group: 'establishments', field: 'taxId' },
      },
      ...(record === 'C010'
        ? [spedField('bookkeepingMethod', 'Forma de escrituração', 3, 'fiscal.establishment.bookkeepingMethod')]
        : []),
    ],
  }
}

function spedContributionsCteGroups(parent: string): DeclarativeFileLayout['groups'] {
  return [
    {
      key: 'cteAcquisitions', label: 'Aquisições por CT-e (D100)', externalPath: 'D100', repeat: true, parent, fields: [
        spedField('operationIndicator', 'Indicador da operação', 2, 'fiscal.document.operationIndicator', 'string', true),
        spedField('issueIndicator', 'Indicador do emitente', 3, 'fiscal.document.issueIndicator'),
        {
          ...spedField('partyCode', 'Código do participante', 4, 'fiscal.document.partyCode'),
          reference: { group: 'participants', field: 'externalCode' },
        },
        spedField('model', 'Modelo do documento', 5, 'fiscal.document.model'),
        spedField('statusCode', 'Situação do documento', 6, 'fiscal.document.statusCode'),
        spedField('series', 'Série', 7, 'fiscal.document.series'),
        spedField('number', 'Número', 9, 'fiscal.document.number', 'string', true),
        spedField('accessKey', 'Chave do CT-e', 10, 'fiscal.document.accessKey'),
        spedField('issuedAt', 'Data de emissão', 11, 'fiscal.document.issuedAt', 'date', true),
        spedField('entryExitAt', 'Data de aquisição', 12, 'fiscal.document.entryExitAt', 'date'),
        spedField('total', 'Valor do documento', 15, 'fiscal.document.total', 'number'),
        spedField('discount', 'Valor do desconto', 16, 'fiscal.document.discount', 'number'),
        spedField('icmsBase', 'Base do ICMS', 19, 'fiscal.document.taxes.icms.base', 'number'),
        spedField('icmsAmount', 'Valor do ICMS', 20, 'fiscal.document.taxes.icms.amount', 'number'),
      ],
    },
    {
      key: 'ctePisDetails', label: 'PIS das aquisições por CT-e (D101)', externalPath: 'D101', repeat: true, parent: 'cteAcquisitions', fields: [
        spedField('freightNature', 'Natureza do frete', 2, 'fiscal.document.taxDetails.freightNature'),
        spedField('itemAmount', 'Valor do item', 3, 'fiscal.document.taxDetails.itemAmount', 'number'),
        spedField('situation', 'CST do PIS', 4, 'fiscal.document.taxDetails.taxes.pis.situation'),
        spedField('creditNature', 'Natureza da base de crédito', 5, 'fiscal.document.taxDetails.creditNature'),
        spedField('base', 'Base do PIS', 6, 'fiscal.document.taxDetails.taxes.pis.base', 'number'),
        spedField('rate', 'Alíquota do PIS', 7, 'fiscal.document.taxDetails.taxes.pis.rate', 'number'),
        spedField('amount', 'Valor do PIS', 8, 'fiscal.document.taxDetails.taxes.pis.amount', 'number'),
        spedField('accountCode', 'Código da conta', 9, 'fiscal.document.taxDetails.accountCode'),
      ],
    },
    {
      key: 'cteCofinsDetails', label: 'COFINS das aquisições por CT-e (D105)', externalPath: 'D105', repeat: true, parent: 'cteAcquisitions', fields: [
        spedField('freightNature', 'Natureza do frete', 2, 'fiscal.document.taxDetails.freightNature'),
        spedField('itemAmount', 'Valor do item', 3, 'fiscal.document.taxDetails.itemAmount', 'number'),
        spedField('situation', 'CST da COFINS', 4, 'fiscal.document.taxDetails.taxes.cofins.situation'),
        spedField('creditNature', 'Natureza da base de crédito', 5, 'fiscal.document.taxDetails.creditNature'),
        spedField('base', 'Base da COFINS', 6, 'fiscal.document.taxDetails.taxes.cofins.base', 'number'),
        spedField('rate', 'Alíquota da COFINS', 7, 'fiscal.document.taxDetails.taxes.cofins.rate', 'number'),
        spedField('amount', 'Valor da COFINS', 8, 'fiscal.document.taxDetails.taxes.cofins.amount', 'number'),
        spedField('accountCode', 'Código da conta', 9, 'fiscal.document.taxDetails.accountCode'),
      ],
    },
  ]
}

function builtInGroups(type: FiscalDocumentType | undefined): DeclarativeFileLayout['groups'] | null {
  if (type === 'nfe') {
    const root = '/nfeProc/NFe/infNFe'
    const item = `${root}/det`
    return [
      {
        key: 'document', label: 'Documento', externalPath: root, repeat: false, fields: [
          digitsLayoutField('accessKey', 'Chave de acesso', `${root}/@Id`, 'fiscal.document.accessKey'),
          layoutField('issuedAt', 'Data de emissão', `${root}/ide/dhEmi||${root}/ide/dEmi`, 'fiscal.document.issuedAt', 'date'),
          layoutField('number', 'Número', `${root}/ide/nNF`, 'fiscal.document.number'),
          layoutField('series', 'Série', `${root}/ide/serie`, 'fiscal.document.series'),
          layoutField('operationNature', 'Natureza da operação', `${root}/ide/natOp`, 'fiscal.document.operationNature'),
          layoutField('operationType', 'Tipo da operação', `${root}/ide/tpNF`, 'fiscal.document.operationType'),
          digitsLayoutField('issuerTaxId', 'CPF/CNPJ do emitente', `${root}/emit/CNPJ||${root}/emit/CPF`, 'fiscal.document.issuer.taxId'),
          layoutField('issuerState', 'UF do emitente', `${root}/emit/enderEmit/UF`, 'fiscal.document.issuer.state'),
          layoutField('issuerTaxRegime', 'Regime tributário do emitente', `${root}/emit/CRT`, 'fiscal.document.issuer.taxRegime'),
          digitsLayoutField('recipientTaxId', 'CPF/CNPJ do destinatário', `${root}/dest/CNPJ||${root}/dest/CPF`, 'fiscal.document.recipient.taxId'),
          layoutField('recipientState', 'UF do destinatário', `${root}/dest/enderDest/UF`, 'fiscal.document.recipient.state'),
          layoutField('total', 'Valor total', `${root}/total/ICMSTot/vNF`, 'fiscal.document.total', 'number'),
          layoutField('icmsBase', 'Base total do ICMS', `${root}/total/ICMSTot/vBC`, 'fiscal.document.taxes.icms.base', 'number'),
          layoutField('icmsAmount', 'Valor total do ICMS', `${root}/total/ICMSTot/vICMS`, 'fiscal.document.taxes.icms.amount', 'number'),
          layoutField('ipiAmount', 'Valor total do IPI', `${root}/total/ICMSTot/vIPI`, 'fiscal.document.taxes.ipi.amount', 'number'),
          layoutField('pisAmount', 'Valor total do PIS', `${root}/total/ICMSTot/vPIS`, 'fiscal.document.taxes.pis.amount', 'number'),
          layoutField('cofinsAmount', 'Valor total da COFINS', `${root}/total/ICMSTot/vCOFINS`, 'fiscal.document.taxes.cofins.amount', 'number'),
          layoutField('ibsBase', 'Base total do IBS', `${root}/total/IBSCBSTot/vBCIBSCBS`, 'fiscal.document.taxes.ibs.base', 'number'),
          layoutField('ibsAmount', 'Valor total do IBS', `${root}/total/IBSCBSTot/gIBS/vIBS`, 'fiscal.document.taxes.ibs.amount', 'number'),
          layoutField('cbsBase', 'Base total da CBS', `${root}/total/IBSCBSTot/vBCIBSCBS`, 'fiscal.document.taxes.cbs.base', 'number'),
          layoutField('cbsAmount', 'Valor total da CBS', `${root}/total/IBSCBSTot/gCBS/vCBS`, 'fiscal.document.taxes.cbs.amount', 'number'),
          layoutField('isAmount', 'Valor total do Imposto Seletivo', `${root}/total/ISTot/vIS`, 'fiscal.document.taxes.is.amount', 'number'),
        ],
      },
      {
        key: 'items', label: 'Itens', externalPath: item, repeat: true, parent: 'document', fields: [
          layoutField('lineNumber', 'Número do item', `${item}/@nItem`, 'fiscal.document.items.lineNumber', 'integer'),
          layoutField('productCode', 'Código do produto', `${item}/prod/cProd`, 'fiscal.document.items.productCode'),
          layoutField('description', 'Descrição', `${item}/prod/xProd`, 'fiscal.document.items.description'),
          layoutField('quantity', 'Quantidade', `${item}/prod/qCom`, 'fiscal.document.items.quantity', 'number'),
          layoutField('uom', 'Unidade', `${item}/prod/uCom`, 'fiscal.document.items.uom'),
          layoutField('unitValue', 'Valor unitário', `${item}/prod/vUnCom`, 'fiscal.document.items.unitValue', 'number'),
          layoutField('total', 'Valor total', `${item}/prod/vProd`, 'fiscal.document.items.total', 'number'),
          layoutField('cfop', 'CFOP', `${item}/prod/CFOP`, 'fiscal.document.items.cfop'),
          layoutField('ncm', 'NCM', `${item}/prod/NCM`, 'fiscal.document.items.ncm'),
          layoutField('icmsSituation', 'CST/CSOSN do ICMS', `${item}/imposto/ICMS/*/CST||${item}/imposto/ICMS/*/CSOSN`, 'fiscal.document.items.taxes.icms.situation'),
          layoutField('icmsBase', 'Base do ICMS', `${item}/imposto/ICMS/*/vBC`, 'fiscal.document.items.taxes.icms.base', 'number'),
          layoutField('icmsRate', 'Alíquota do ICMS', `${item}/imposto/ICMS/*/pICMS`, 'fiscal.document.items.taxes.icms.rate', 'number'),
          layoutField('icmsAmount', 'Valor do ICMS', `${item}/imposto/ICMS/*/vICMS`, 'fiscal.document.items.taxes.icms.amount', 'number'),
          layoutField('ipiSituation', 'CST do IPI', `${item}/imposto/IPI/*/CST`, 'fiscal.document.items.taxes.ipi.situation'),
          layoutField('ipiBase', 'Base do IPI', `${item}/imposto/IPI/*/vBC`, 'fiscal.document.items.taxes.ipi.base', 'number'),
          layoutField('ipiRate', 'Alíquota do IPI', `${item}/imposto/IPI/*/pIPI`, 'fiscal.document.items.taxes.ipi.rate', 'number'),
          layoutField('ipiAmount', 'Valor do IPI', `${item}/imposto/IPI/*/vIPI`, 'fiscal.document.items.taxes.ipi.amount', 'number'),
          layoutField('pisSituation', 'CST do PIS', `${item}/imposto/PIS/*/CST`, 'fiscal.document.items.taxes.pis.situation'),
          layoutField('pisBase', 'Base do PIS', `${item}/imposto/PIS/*/vBC`, 'fiscal.document.items.taxes.pis.base', 'number'),
          layoutField('pisRate', 'Alíquota do PIS', `${item}/imposto/PIS/*/pPIS`, 'fiscal.document.items.taxes.pis.rate', 'number'),
          layoutField('pisAmount', 'Valor do PIS', `${item}/imposto/PIS/*/vPIS`, 'fiscal.document.items.taxes.pis.amount', 'number'),
          layoutField('cofinsSituation', 'CST da COFINS', `${item}/imposto/COFINS/*/CST`, 'fiscal.document.items.taxes.cofins.situation'),
          layoutField('cofinsBase', 'Base da COFINS', `${item}/imposto/COFINS/*/vBC`, 'fiscal.document.items.taxes.cofins.base', 'number'),
          layoutField('cofinsRate', 'Alíquota da COFINS', `${item}/imposto/COFINS/*/pCOFINS`, 'fiscal.document.items.taxes.cofins.rate', 'number'),
          layoutField('cofinsAmount', 'Valor da COFINS', `${item}/imposto/COFINS/*/vCOFINS`, 'fiscal.document.items.taxes.cofins.amount', 'number'),
          layoutField('ibsSituation', 'CST do IBS/CBS', `${item}/imposto/IBSCBS/CST`, 'fiscal.document.items.taxes.ibs.situation'),
          layoutField('ibsClassification', 'Classificação tributária do IBS/CBS', `${item}/imposto/IBSCBS/cClassTrib`, 'fiscal.document.items.taxes.ibs.classificationCode'),
          layoutField('ibsBase', 'Base do IBS', `${item}/imposto/IBSCBS/gIBSCBS/vBC`, 'fiscal.document.items.taxes.ibs.base', 'number'),
          layoutField('ibsStateRate', 'Alíquota do IBS estadual', `${item}/imposto/IBSCBS/gIBSCBS/gIBSUF/pIBSUF`, 'fiscal.document.items.taxes.ibs.stateRate', 'number'),
          layoutField('ibsStateAmount', 'Valor do IBS estadual', `${item}/imposto/IBSCBS/gIBSCBS/gIBSUF/vIBSUF`, 'fiscal.document.items.taxes.ibs.stateAmount', 'number'),
          layoutField('ibsMunicipalRate', 'Alíquota do IBS municipal', `${item}/imposto/IBSCBS/gIBSCBS/gIBSMun/pIBSMun`, 'fiscal.document.items.taxes.ibs.municipalRate', 'number'),
          layoutField('ibsMunicipalAmount', 'Valor do IBS municipal', `${item}/imposto/IBSCBS/gIBSCBS/gIBSMun/vIBSMun`, 'fiscal.document.items.taxes.ibs.municipalAmount', 'number'),
          layoutField('ibsAmount', 'Valor total do IBS', `${item}/imposto/IBSCBS/gIBSCBS/vIBS`, 'fiscal.document.items.taxes.ibs.amount', 'number'),
          layoutField('cbsSituation', 'CST da CBS', `${item}/imposto/IBSCBS/CST`, 'fiscal.document.items.taxes.cbs.situation'),
          layoutField('cbsClassification', 'Classificação tributária da CBS', `${item}/imposto/IBSCBS/cClassTrib`, 'fiscal.document.items.taxes.cbs.classificationCode'),
          layoutField('cbsBase', 'Base da CBS', `${item}/imposto/IBSCBS/gIBSCBS/vBC`, 'fiscal.document.items.taxes.cbs.base', 'number'),
          layoutField('cbsRate', 'Alíquota da CBS', `${item}/imposto/IBSCBS/gIBSCBS/gCBS/pCBS`, 'fiscal.document.items.taxes.cbs.rate', 'number'),
          layoutField('cbsAmount', 'Valor da CBS', `${item}/imposto/IBSCBS/gIBSCBS/gCBS/vCBS`, 'fiscal.document.items.taxes.cbs.amount', 'number'),
          layoutField('isSituation', 'CST do Imposto Seletivo', `${item}/imposto/IS/CSTIS`, 'fiscal.document.items.taxes.is.situation'),
          layoutField('isClassification', 'Classificação tributária do Imposto Seletivo', `${item}/imposto/IS/cClassTribIS`, 'fiscal.document.items.taxes.is.classificationCode'),
          layoutField('isBase', 'Base do Imposto Seletivo', `${item}/imposto/IS/vBCIS||${item}/imposto/IS/*/vBCIS`, 'fiscal.document.items.taxes.is.base', 'number'),
          layoutField('isRate', 'Alíquota do Imposto Seletivo', `${item}/imposto/IS/pIS||${item}/imposto/IS/*/pIS`, 'fiscal.document.items.taxes.is.rate', 'number'),
          layoutField('isAmount', 'Valor do Imposto Seletivo', `${item}/imposto/IS/vIS||${item}/imposto/IS/*/vIS`, 'fiscal.document.items.taxes.is.amount', 'number'),
        ],
      },
    ]
  }
  if (type === 'cte') {
    const root = '/cteProc/CTe/infCte'
    return [{ key: 'document', label: 'Documento', externalPath: root, repeat: false, fields: [
      digitsLayoutField('accessKey', 'Chave de acesso', `${root}/@Id`, 'fiscal.document.accessKey'),
      layoutField('issuedAt', 'Data de emissão', `${root}/ide/dhEmi`, 'fiscal.document.issuedAt', 'date'),
      layoutField('number', 'Número', `${root}/ide/nCT`, 'fiscal.document.number'),
      layoutField('series', 'Série', `${root}/ide/serie`, 'fiscal.document.series'),
      layoutField('operationNature', 'Natureza da operação', `${root}/ide/natOp`, 'fiscal.document.operationNature'),
      digitsLayoutField('issuerTaxId', 'CPF/CNPJ do emitente', `${root}/emit/CNPJ||${root}/emit/CPF`, 'fiscal.document.issuer.taxId'),
      digitsLayoutField('recipientTaxId', 'CPF/CNPJ do destinatário', `${root}/dest/CNPJ||${root}/dest/CPF||${root}/rem/CNPJ||${root}/rem/CPF`, 'fiscal.document.recipient.taxId'),
      layoutField('total', 'Valor total', `${root}/vPrest/vTPrest`, 'fiscal.document.total', 'number'),
      layoutField('icmsSituation', 'CST do ICMS', `${root}/imp/ICMS/*/CST`, 'fiscal.document.taxes.icms.situation'),
      layoutField('icmsBase', 'Base do ICMS', `${root}/imp/ICMS/*/vBC`, 'fiscal.document.taxes.icms.base', 'number'),
      layoutField('icmsRate', 'Alíquota do ICMS', `${root}/imp/ICMS/*/pICMS||${root}/imp/ICMS/*/pICMSOutraUF`, 'fiscal.document.taxes.icms.rate', 'number'),
      layoutField('icmsAmount', 'Valor do ICMS', `${root}/imp/ICMS/*/vICMS||${root}/imp/ICMS/*/vICMSOutraUF`, 'fiscal.document.taxes.icms.amount', 'number'),
    ] }]
  }
  if (type === 'nfse_nacional') {
    const root = '/NFSe/infNFSe'
    return [{ key: 'document', label: 'Documento', externalPath: root, repeat: false, fields: [
      digitsLayoutField('accessKey', 'Identificador', `${root}/@Id`, 'fiscal.document.accessKey'),
      layoutField('number', 'Número', `${root}/nNFSe`, 'fiscal.document.number'),
      layoutField('issuedAt', 'Data de emissão', `${root}/dhProc`, 'fiscal.document.issuedAt', 'date'),
      digitsLayoutField('issuerTaxId', 'CPF/CNPJ do prestador', `${root}/prest/CNPJ||${root}/prest/CPF`, 'fiscal.document.issuer.taxId'),
      digitsLayoutField('recipientTaxId', 'CPF/CNPJ do tomador', `${root}/toma/CNPJ||${root}/toma/CPF`, 'fiscal.document.recipient.taxId'),
      layoutField('total', 'Valor total', `${root}/valores/vLiq`, 'fiscal.document.total', 'number'),
    ] }]
  }
  if (type === 'sped_fiscal') {
    return [
      ...spedFiscalHeaderGroup(),
      ...spedReferenceGroups(true),
      ...spedNfeGroups(true),
      ...spedFiscalCteGroups(),
    ]
  }
  if (type === 'sped_contributions') {
    return [
      ...spedContributionsHeaderGroups(),
      ...spedReferenceGroups(false),
      spedContributionsBlockEstablishment('nfeEstablishments', 'C010', 'Estabelecimentos do bloco C (C010)'),
      ...spedNfeGroups(false, 'nfeEstablishments'),
      ...spedContributionsConsolidatedNfeGroups(),
      spedContributionsBlockEstablishment('cteEstablishments', 'D010', 'Estabelecimentos do bloco D (D010)'),
      ...spedContributionsCteGroups('cteEstablishments'),
    ]
  }
  return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function slug(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'novo_leiaute'
}

function pathName(path: string) {
  const last = path.split('/').filter(Boolean).pop() || 'campo'
  return last.replace(/^@/, '')
}

export function createLayoutDefinitionSkeleton(options?: {
  type?: FiscalDocumentType
  name?: string
  direction?: FileLayoutDirection
  format?: FileLayoutFormat
}): DeclarativeFileLayout {
  const type = options?.type
  const name = options?.name || (type && BUILT_IN_NAMES[type]) || 'Novo leiaute'
  const format = options?.format || (type && BUILT_IN_FORMATS[type]) || 'xml'
  const presetKey = type && BUILT_IN_NAMES[type] ? type : null
  const standardGroups = builtInGroups(type)
  return {
    schemaVersion: 1,
    key: presetKey || slug(name),
    name,
    layoutVersion: '1.0.0',
    direction: options?.direction || 'import',
    format,
    description: (type && BUILT_IN_DESCRIPTIONS[type]) || '',
    detection: (type && BUILT_IN_DETECTION[type]) || {},
    parser: format === 'xml'
      ? { encoding: 'UTF-8', ignoreNamespacePrefix: true }
      : {
          encoding: 'UTF-8',
          delimiter: '|',
          recordDelimiter: '\\r?\\n',
          decimalSeparator: ',',
          dateFormats: type === 'sped_fiscal' || type === 'sped_contributions' ? ['ddMMyyyy'] : [],
        },
    groups: standardGroups || [{
      key: format === 'xml' ? 'document' : 'records',
      label: format === 'xml' ? 'Documento' : 'Registros',
      externalPath: '',
      repeat: format !== 'xml',
      fields: [],
    }],
  }
}

export function parseLayoutDefinition(text: string): DeclarativeFileLayout {
  const parsed: unknown = JSON.parse(text)
  if (!isRecord(parsed)) throw new Error('layout_json_root_invalid')
  return parsed as unknown as DeclarativeFileLayout
}

export function validateLayoutDefinition(value: unknown): FileLayoutValidationResult {
  const errors: string[] = []
  const warnings: string[] = []
  if (!isRecord(value)) return { valid: false, errors: ['root'], warnings }
  if (value.schemaVersion !== 1) errors.push('schemaVersion')
  if (typeof value.key !== 'string' || value.key.trim().length < 2) errors.push('key')
  if (typeof value.name !== 'string' || value.name.trim().length < 3) errors.push('name')
  if (typeof value.layoutVersion !== 'string' || !value.layoutVersion.trim()) errors.push('layoutVersion')
  if (!DIRECTIONS.includes(value.direction as FileLayoutDirection)) errors.push('direction')
  if (!FORMATS.includes(value.format as FileLayoutFormat)) errors.push('format')
  if (!isRecord(value.detection)) errors.push('detection')
  if (!isRecord(value.parser)) errors.push('parser')
  if (!Array.isArray(value.groups) || value.groups.length === 0) errors.push('groups')
  if (Array.isArray(value.groups) && value.groups.length > MAX_GROUPS) errors.push('groupsLimit')

  const groupKeys = new Set<string>()
  const fieldKeys = new Set<string>()
  let fieldCount = 0
  if (Array.isArray(value.groups)) value.groups.forEach((rawGroup, groupIndex) => {
    if (!isRecord(rawGroup)) { errors.push(`groups.${groupIndex}`); return }
    const groupKey = typeof rawGroup.key === 'string' ? rawGroup.key.trim() : ''
    if (!groupKey) errors.push(`groups.${groupIndex}.key`)
    else if (groupKeys.has(groupKey)) errors.push(`groups.${groupIndex}.keyDuplicate`)
    else groupKeys.add(groupKey)
    if (typeof rawGroup.label !== 'string' || !rawGroup.label.trim()) errors.push(`groups.${groupIndex}.label`)
    if (typeof rawGroup.externalPath !== 'string' || !rawGroup.externalPath.trim()) errors.push(`groups.${groupIndex}.externalPath`)
    if (typeof rawGroup.repeat !== 'boolean') errors.push(`groups.${groupIndex}.repeat`)
    if (rawGroup.required !== undefined && typeof rawGroup.required !== 'boolean') errors.push(`groups.${groupIndex}.required`)
    if (!Array.isArray(rawGroup.fields)) { errors.push(`groups.${groupIndex}.fields`); return }
    fieldCount += rawGroup.fields.length
    rawGroup.fields.forEach((rawField, fieldIndex) => {
      if (!isRecord(rawField)) { errors.push(`groups.${groupIndex}.fields.${fieldIndex}`); return }
      const prefix = `groups.${groupIndex}.fields.${fieldIndex}`
      const fieldKey = typeof rawField.key === 'string' ? `${groupKey}.${rawField.key.trim()}` : ''
      if (!fieldKey || fieldKey.endsWith('.')) errors.push(`${prefix}.key`)
      else if (fieldKeys.has(fieldKey)) errors.push(`${prefix}.keyDuplicate`)
      else fieldKeys.add(fieldKey)
      if (typeof rawField.label !== 'string' || !rawField.label.trim()) errors.push(`${prefix}.label`)
      if (typeof rawField.externalPath !== 'string' || !rawField.externalPath.trim()) errors.push(`${prefix}.externalPath`)
      if (typeof rawField.internalField !== 'string' || !rawField.internalField.trim()) errors.push(`${prefix}.internalField`)
      else if (!findSemanticFieldDefinition(rawField.internalField)) errors.push(`${prefix}.internalFieldUnknown`)
      if (!DATA_TYPES.includes(rawField.dataType as FileLayoutDataType)) errors.push(`${prefix}.dataType`)
      if (rawField.transform !== undefined && !isRecord(rawField.transform)) errors.push(`${prefix}.transform`)
    })
  })
  if (fieldCount === 0) errors.push('fieldsRequired')
  if (fieldCount > MAX_FIELDS) errors.push('fieldsLimit')

  if (Array.isArray(value.groups)) value.groups.forEach((rawGroup, groupIndex) => {
    if (!isRecord(rawGroup) || rawGroup.parent === undefined || rawGroup.parent === null || rawGroup.parent === '') return
    if (typeof rawGroup.parent !== 'string' || !groupKeys.has(rawGroup.parent)) errors.push(`groups.${groupIndex}.parent`)
  })
  if (Array.isArray(value.groups)) value.groups.forEach((rawGroup, groupIndex) => {
    if (!isRecord(rawGroup) || !Array.isArray(rawGroup.fields)) return
    rawGroup.fields.forEach((rawField, fieldIndex) => {
      if (!isRecord(rawField) || rawField.reference === undefined) return
      const prefix = `groups.${groupIndex}.fields.${fieldIndex}.reference`
      if (!isRecord(rawField.reference)) { errors.push(prefix); return }
      const referenceGroup = typeof rawField.reference.group === 'string' ? rawField.reference.group.trim() : ''
      const referenceField = typeof rawField.reference.field === 'string' ? rawField.reference.field.trim() : ''
      if (!referenceGroup || !referenceField || !fieldKeys.has(`${referenceGroup}.${referenceField}`)) {
        errors.push(prefix)
      }
    })
  })
  return { valid: errors.length === 0, errors, warnings }
}

export function definitionFromFileLayout(layout: FileLayoutWithVersion): DeclarativeFileLayout {
  const storedDefinition = layout.latest_version?.parser_config?.layout_definition
  if (validateLayoutDefinition(storedDefinition).valid) return storedDefinition as unknown as DeclarativeFileLayout

  const fieldsByGroup = new Map<'document' | 'items', DeclarativeLayoutField[]>()
  fieldsByGroup.set('document', [])
  fieldsByGroup.set('items', [])
  layout.fields.forEach((field) => {
    const mapping = layout.mappings.find((candidate) => candidate.layout_field_id === field.id)
    const groupKey = mapping?.target_entity === 'movement_item' ? 'items' : 'document'
    const internalField = mapping
      ? findSemanticField(mapping.target_entity, mapping.target_field) || `${mapping.target_entity}.${mapping.target_field}`
      : ''
    fieldsByGroup.get(groupKey)?.push({
      key: field.field_key,
      label: field.label,
      externalPath: field.source_path,
      internalField,
      dataType: DATA_TYPES.includes(field.data_type as FileLayoutDataType) ? field.data_type as FileLayoutDataType : 'string',
      required: field.required,
      transform: mapping?.transform || field.config,
    })
  })
  const itemLoop = layout.latest_version?.parser_config?.item_loop_path
  const groups: DeclarativeFileLayout['groups'] = [{
    key: 'document', label: 'Documento', externalPath: layout.root_name ? `/${layout.root_name}` : '', repeat: false,
    fields: fieldsByGroup.get('document') || [],
  }]
  if ((fieldsByGroup.get('items') || []).length > 0) groups.push({
    key: 'items', label: 'Itens', externalPath: typeof itemLoop === 'string' ? itemLoop : '', repeat: true, parent: 'document',
    fields: fieldsByGroup.get('items') || [],
  })
  const presetKey = layout.latest_version?.parser_config?.preset_key
  return {
    schemaVersion: 1,
    key: typeof presetKey === 'string' ? presetKey : layout.key,
    name: layout.name,
    layoutVersion: String(layout.latest_version?.version || 1),
    direction: 'import',
    format: layout.format,
    detection: { rootName: layout.root_name, namespaceUri: layout.namespace_uri },
    parser: {
      encoding: typeof layout.latest_version?.parser_config?.encoding === 'string' ? layout.latest_version.parser_config.encoding : 'UTF-8',
      ignoreNamespacePrefix: layout.latest_version?.parser_config?.ignore_namespace_prefix !== false,
    },
    groups,
  }
}

export function saveInputFromDefinition(
  definition: DeclarativeFileLayout,
  unitId: string,
  layoutId: string | null,
): SaveFileLayoutInput {
  const importEnabled = definition.direction !== 'export'
  const repeatedGroup = definition.groups.find((group) => group.repeat)
  const persistentSourcePath = (groupPath: string, fieldPath: string) => definition.format === 'xml'
    ? fieldPath
    : `${groupPath}/${fieldPath}`
  const fields = definition.groups.flatMap((group) => group.fields.map((field, index) => ({
    sourcePath: persistentSourcePath(group.externalPath, field.externalPath),
    fieldKey: `${group.key}_${field.key}`.replace(/[^a-zA-Z0-9_]+/g, '_'),
    label: field.label,
    nodeKind: field.externalPath.split('/').pop()?.startsWith('@') ? 'attribute' as const : 'element' as const,
    dataType: field.dataType,
    required: Boolean(field.required),
    isCollection: group.repeat,
    ordinal: index,
  })))
  const mappings = importEnabled ? definition.groups.flatMap((group) => group.fields.flatMap((field) => {
    const target = findMappingTargetBySemanticField(field.internalField)
    if (!target) return []
    return [{
      sourcePath: persistentSourcePath(group.externalPath, field.externalPath),
      targetEntity: target.entity,
      targetField: target.field,
      loopPath: target.entity === 'movement_item' ? group.externalPath || repeatedGroup?.externalPath || null : null,
    }]
  })) : []
  return {
    unitId,
    contextKey: 'accounting',
    layoutId,
    name: definition.name,
    format: definition.format,
    rootName: definition.detection.rootName || '',
    namespaceUri: definition.detection.namespaceUri || null,
    parserConfig: {
      schema_version: definition.schemaVersion,
      direction: definition.direction,
      encoding: definition.parser.encoding || 'UTF-8',
      processing_mode: repeatedGroup ? 'header_and_items' : 'total_only',
      item_loop_path: repeatedGroup?.externalPath || null,
      preset_key: definition.key,
      layout_definition: definition,
    },
    fields,
    mappings,
  }
}

export function formatLayoutDefinition(definition: DeclarativeFileLayout) {
  return JSON.stringify(definition, null, 2)
}

export function semanticFieldCatalog() {
  return semanticFieldDefinitions().map((field) => ({
    field: field.field,
    type: field.valueType,
    scope: field.scope,
  }))
}

export function generateLayoutAiInstructions(definition: DeclarativeFileLayout, locale: string) {
  const language = locale.startsWith('en') ? 'English' : locale.startsWith('es') ? 'Spanish' : 'Brazilian Portuguese'
  const directionText = definition.direction === 'import'
    ? 'external file to the MyERP semantic model'
    : definition.direction === 'export'
      ? 'MyERP semantic model to the external file'
      : 'both directions between the external file and the MyERP semantic model'
  return [
    `Answer in ${language}. Act as a specialist in structured fiscal and business file layouts.`,
    '',
    `Analyze the file attached by the user and complete the JSON layout skeleton below for ${directionText}.`,
    `External format: ${definition.format}.`,
    '',
    'Mandatory rules:',
    '1. Return only one valid JSON object. Do not use Markdown fences and do not add explanations.',
    '2. Preserve schemaVersion = 1 and use only the properties already present in the skeleton.',
    '3. Use exact external paths found in the attached file. For XML, include the full element or attribute path.',
    '4. Identify repeated groups, parent relationships and cross-record references. Items and taxes must remain related to the correct document and product.',
    '5. Use only internalField values from the catalog below. Do not invent internal MyERP fields.',
    '6. Do not invent data that is absent from the example. Omit fields that cannot be mapped safely.',
    '7. Do not include scripts, expressions, SQL, JavaScript, HTML or executable code.',
    '8. Keep tax treatments separate by tax and preserve CST or equivalent code, base, rate and amount when available.',
    '9. For XML, group.externalPath and field.externalPath must contain the full path used in the file.',
    '10. For delimited text, group.externalPath is the record code and field.externalPath uses field:N, where N is one-based and includes the record code as field 1.',
    '11. For fixed-width text, group.externalPath is the line prefix and field.externalPath uses chars:START-END with one-based inclusive positions.',
    '12. For positional or delimited text, identify record hierarchy, delimiter, encoding and repeated records.',
    '13. The result will be validated by MyERP before it can be published.',
    '',
    'Allowed internal semantic fields:',
    JSON.stringify(semanticFieldCatalog(), null, 2),
    '',
    'JSON layout skeleton to complete:',
    formatLayoutDefinition(definition),
  ].join('\n')
}

export function cloneLayoutDefinition(definition: DeclarativeFileLayout, copyName: string) {
  return { ...definition, key: `${slug(definition.key)}_copy`, name: copyName, layoutVersion: '1.0.0' }
}
